// src/manual-presets.ts
import { useCallback as useCallback4, useContext } from "react";

// src/ManualContext.tsx
import * as React from "react";

// src/internal/useApi.ts
import { useCallback } from "react";
var encodeUri = (params) => Object.entries(params).map(
  (param) => `${encodeURIComponent(param[0])}=${encodeURIComponent(param[1])}`
).join("&");
function useApi(root, user, university, env, referencing) {
  const { id: userId } = user;
  const getIndexArticles = useCallback(async () => {
    const props = { university, referencing };
    const url = `${root}/articles?` + encodeUri(props);
    return fetch(url).then((resp) => resp.json()).catch(() => []);
  }, [root, university, referencing]);
  const getSearchResults = useCallback(
    async (query) => {
      const url = `${root}/search?` + encodeUri({ university, q: query });
      return fetch(url).then((resp) => resp.json()).catch(() => []);
    },
    [root, university]
  );
  const postVote = useCallback(
    (articleId, vote) => {
      const search = encodeUri({
        userId,
        university: `${university}:${env}`,
        vote
      });
      const url = `${root}/articles/${articleId}/like?${search}`;
      return fetch(url, { method: "POST" });
    },
    [root, userId, university, env]
  );
  return { getIndexArticles, getSearchResults, postVote };
}

// src/useHubspotSearch.ts
import { useCallback as useCallback2 } from "react";
function useHubspotSearch(portalId) {
  return useCallback2(
    async (query) => {
      const url = new URL("https://api.hubapi.com/contentsearch/v2/search");
      url.searchParams.append("portalId", portalId);
      url.searchParams.append("pathPrefix", "students");
      url.searchParams.append("type", "KNOWLEDGE_ARTICLE");
      url.searchParams.append("length", "LONG");
      url.searchParams.append("limit", "100");
      url.searchParams.append("term", query);
      return fetch(url.toString()).then((resp) => resp.json()).then((result) => result.results).catch(() => []);
    },
    [portalId]
  );
}

// src/ManualContext.tsx
import { jsx } from "react/jsx-runtime";
var defaultValue = {
  isOpen: false,
  setOpen: () => null,
  page: {
    pageType: "home" /* Home */
  },
  changePage: () => null,
  goBack: () => null,
  onVote: () => null,
  query: "",
  makeQuery: () => null,
  results: [],
  hsSearchResults: [],
  articles: [],
  // onboarding
  user: {
    id: "",
    givenName: "",
    familyName: ""
  },
  // presets
  presets: {
    referencing_style: "",
    referencing_overview: "",
    academic_integrity: "",
    generative_ai: "",
    quoting: "",
    paraphrasing: "",
    declaration: "",
    start: ""
  },
  referencingStyle: ""
};
var ManualContext = React.createContext(defaultValue);
var ManualProvider = (props) => {
  const {
    user,
    referencingStyle,
    logger,
    endpoint,
    env,
    tenant,
    portalId = "5206287"
  } = props;
  const [isOpen, setOpen] = React.useState(defaultValue.isOpen);
  const [articles, setArticles] = React.useState(
    defaultValue.articles
  );
  const [page, setPage] = React.useState(defaultValue.page);
  const [pageStack, setPageStack] = React.useState([defaultValue.page]);
  const [query, setQuery] = React.useState(defaultValue.query);
  const [results, setResults] = React.useState(defaultValue.results);
  const [hsSearchResults, setHsResults] = React.useState(
    defaultValue.hsSearchResults
  );
  const { getIndexArticles, getSearchResults, postVote } = useApi(
    endpoint,
    user,
    tenant,
    env,
    referencingStyle
  );
  const getHubspotResults = useHubspotSearch(portalId);
  const presets = manualPresets(referencingStyle);
  React.useEffect(() => {
    getIndexArticles().then((data) => setArticles(data));
  }, [getIndexArticles, setArticles]);
  const logEvent = React.useCallback(
    (newPage) => {
      let location;
      let url;
      switch (newPage.pageType) {
        case "article" /* Article */: {
          location = newPage.selectedArticle.slug;
          url = newPage.selectedArticle.url;
          break;
        }
        case "index" /* Category */: {
          location = newPage.categoryName;
          break;
        }
        case "result" /* Results */: {
          location = query;
          break;
        }
      }
      logger({
        type: "MANUAL_VIEW" /* View */,
        page: newPage.pageType,
        location,
        url
      });
    },
    [logger, query]
  );
  const changePage = React.useCallback(
    (newPage) => {
      if (newPage.pageType === "article" /* Article */ && newPage.selectedArticle.url) {
        window.open(newPage.selectedArticle.url, "_blank");
      } else {
        setPage(newPage);
        switch (newPage.pageType) {
          case "home" /* Home */: {
            setPageStack([newPage]);
            break;
          }
          case "result" /* Results */: {
            if (!(pageStack[pageStack.length - 1].pageType === "result" /* Results */))
              setPageStack([...pageStack, newPage]);
            break;
          }
          default: {
            setPageStack([...pageStack, newPage]);
            break;
          }
        }
      }
      logEvent(newPage);
    },
    [pageStack, logEvent]
  );
  const goBack = React.useCallback(() => {
    const newStack = pageStack.slice(0, pageStack.length - 1);
    setPageStack(
      newStack.length === 0 ? [{ pageType: "home" /* Home */ }] : newStack
    );
    const newPage = newStack[newStack.length - 1];
    setPage(newPage ? newPage : { pageType: "home" /* Home */ });
    logEvent(newPage ? newPage : { pageType: "home" /* Home */ });
  }, [pageStack, logEvent]);
  const makeQuery = React.useCallback(
    async (query2) => {
      setQuery(query2);
      if (query2 === "") {
        goBack();
      } else {
        const [articles2, hubSpotArticles] = await Promise.all([
          getSearchResults(query2),
          getHubspotResults(query2)
        ]);
        if (articles2) setResults(articles2);
        setHsResults(hubSpotArticles);
        changePage({
          pageType: "result" /* Results */
        });
      }
    },
    [getSearchResults, goBack, changePage, getHubspotResults]
  );
  const onVote = React.useCallback(
    (vote, article) => {
      const updatedArticles = articles.map(
        (a) => a.id === article.id ? { ...a, vote } : a
      );
      setArticles(updatedArticles);
      if (vote !== "" /* Unvote */) {
        postVote(article.id, vote);
      }
    },
    [articles, postVote]
  );
  const value = {
    user,
    isOpen,
    setOpen,
    // page
    page,
    changePage,
    goBack,
    // query
    query,
    makeQuery,
    results,
    hsSearchResults,
    // vote
    onVote,
    // articles
    articles,
    // preset
    presets,
    referencingStyle: referencingStyle.toLowerCase()
  };
  return /* @__PURE__ */ jsx(ManualContext.Provider, { value, children: props.children });
};

// src/manual-presets.ts
function manualPresets(referencingStyle) {
  const style = referencingStyle.toLowerCase();
  const hasReferencingStyle = style !== "" && style !== "no_referencing";
  const stylePath = hasReferencingStyle ? `/${style}/` : "/no_referencing/";
  const referencingList = `referencing${stylePath}list.md`;
  return {
    referencing_style: hasReferencingStyle ? `referencing${stylePath}styles.md` : referencingList,
    referencing_overview: hasReferencingStyle ? `referencing${stylePath}overview.md` : referencingList,
    academic_integrity: "academic_integrity/integrity_url.md",
    generative_ai: "generative_ai/generative_ai_and_academic_integrity.md",
    quoting: `referencing${stylePath}quoting.md`,
    paraphrasing: "referencing/paraphrasing.md",
    declaration: "using_cadmus/submit_dec.md",
    start: "using_cadmus/getting_started.md"
  };
}
function useManualPreset() {
  const { articles, presets, setOpen, changePage } = useContext(ManualContext);
  const openPreset = useCallback4(
    (preset) => {
      const selectedArticle = presets[preset] && articles.find((a) => a.slug === presets[preset]);
      if (selectedArticle) {
        if (!selectedArticle.url) setOpen(true);
        changePage({
          pageType: "article" /* Article */,
          selectedArticle
        });
      }
    },
    [articles, setOpen, changePage, presets]
  );
  return openPreset;
}

// src/useManualOpen.tsx
import { useContext as useContext2, useMemo } from "react";
function useManualOpen() {
  const { isOpen } = useContext2(ManualContext);
  return useMemo(() => isOpen, [isOpen]);
}

// src/manual-modal/triggers/CircularTrigger.tsx
import { forwardRef, useContext as useContext3 } from "react";
import { styled } from "styled-components";
import { Icon, IconButton, levels } from "@vericus/cadmus-ui";
import { jsx as jsx2 } from "react/jsx-runtime";
var CircularTrigger = forwardRef(
  (_, ref) => {
    const { setOpen, changePage } = useContext3(ManualContext);
    return /* @__PURE__ */ jsx2(
      ManualIconButton,
      {
        shape: "circle",
        ref,
        onClick: () => {
          setOpen(true);
          changePage({ pageType: "home" /* Home */ });
        },
        "aria-label": "Cadmus Manual",
        tooltipPosition: "left",
        children: /* @__PURE__ */ jsx2(Icon, { iconName: "Book" })
      }
    );
  }
);
var ManualIconButton = styled(IconButton)`
  background: white;
  box-shadow: ${levels.three};
`;

// src/manual-modal/triggers/index.ts
var ManualTriggers = {
  CircularTrigger
};

// src/manual-modal/modal/ManualModal.tsx
import { useContext as useContext6, useEffect as useEffect3, useRef as useRef2 } from "react";
import { Divider as Divider2 } from "@vericus/cadmus-ui";
import * as RadixDialog3 from "@radix-ui/react-dialog";

// src/components/helper/Header.tsx
import { styled as styled2 } from "styled-components";
import { ArrowLeftIcon, CrossIcon } from "@vericus/cadmus-icons";
import { Button, ControlButton, Text } from "@vericus/cadmus-ui";
import * as RadixDialog from "@radix-ui/react-dialog";
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var Container = styled2.div`
  width: 100%;
  padding: 0px 24px 8px 24px;
  box-sizing: border-box;
  justify-self: flex-start;
  display: flex;
  flex-direction: column;
  flex: none;
`;
var TitleActions = styled2.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  margin-bottom: 8px;
`;
var Title = styled2.div`
  display: flex;
  flex-direction: column;
  padding: 0px 4px;
  gap: 4px;
  flex: auto;
`;
var Header = ({
  title,
  subtitle,
  onClose,
  goBack
}) => {
  return /* @__PURE__ */ jsxs(Container, { children: [
    /* @__PURE__ */ jsxs(TitleActions, { children: [
      /* @__PURE__ */ jsx3("div", { children: goBack && /* @__PURE__ */ jsx3(
        Button,
        {
          kind: "ghost",
          size: "sm",
          onClick: goBack,
          "aria-label": "go back",
          icon: /* @__PURE__ */ jsx3(ArrowLeftIcon, { size: 20, label: "" }),
          children: "Back"
        }
      ) }),
      /* @__PURE__ */ jsx3(RadixDialog.DialogClose, { asChild: true, children: /* @__PURE__ */ jsx3(
        ControlButton,
        {
          icon: /* @__PURE__ */ jsx3(CrossIcon, { size: 16, label: "close manual", onClick: onClose })
        }
      ) })
    ] }),
    /* @__PURE__ */ jsxs(Title, { children: [
      /* @__PURE__ */ jsx3(
        Text,
        {
          kind: "headingFour",
          style: {
            flex: "auto",
            overflow: "hidden",
            textOverflow: "ellipsis"
          },
          children: title
        }
      ),
      /* @__PURE__ */ jsx3(Text, { kind: "label", as: "span", children: subtitle })
    ] })
  ] });
};

// src/components/helper/Help.tsx
import { styled as styled3 } from "styled-components";
import { Button as Button2, Text as Text2 } from "@vericus/cadmus-ui";
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var Container2 = styled3.div`
  height: 56px;
  padding: 9px 27px;
  border-top: 1px solid ${(p) => p.theme.divider};
  box-sizing: border-box;
  flex: none;

  justify-self: flex-end;
  display: flex;
  justify-content: space-between;
  align-items: center;

  background-color: ${(p) => p.theme.colors.orange100};
`;
var Help = ({ onAskHelp }) => /* @__PURE__ */ jsxs2(Container2, { children: [
  /* @__PURE__ */ jsx4(Text2, { kind: "bodyMd", children: "Need help?" }),
  /* @__PURE__ */ jsx4(Button2, { kind: "secondary", onClick: onAskHelp, children: "Contact Support" })
] });

// src/components/helper/SearchBox.tsx
import { useEffect as useEffect2, useState as useState2 } from "react";
import { styled as styled4 } from "styled-components";
import { jsx as jsx5 } from "react/jsx-runtime";
var Search = styled4.input`
  width: 100%;
  height: 54px;
  padding: 0px 27px;

  border: none;
  outline: none;

  font-size: 14px;
  color: ${(p) => p.theme.text.main};
  background: ${(p) => p.theme.colors.orange100};

  &::placeholder {
    color: ${(p) => p.theme.text.shade1};
  }

  &:focus-visible {
    ${(p) => p.theme.focusInset};
  }
`;
var SearchBox = ({ query, makeQuery }) => {
  const [internalQuery, setInternalQuery] = useDebouncedDispatch(
    query,
    makeQuery,
    400
  );
  const handleChange = (event) => {
    const newQuery = event.target.value;
    setInternalQuery(newQuery);
  };
  return /* @__PURE__ */ jsx5(
    Search,
    {
      type: "search",
      value: internalQuery,
      onChange: handleChange,
      placeholder: "Search help docs or university resources",
      "aria-label": "Search help docs or university resources"
    }
  );
};
function useDebouncedDispatch(value, dispatch, debounceMs = 400) {
  const [internalValue, setInternalValue] = useState2(value);
  useEffect2(() => setInternalValue(value), [value, setInternalValue]);
  useEffect2(() => {
    const timer = setTimeout(() => {
      if (value !== internalValue) {
        dispatch(internalValue);
      }
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [internalValue, value, dispatch, debounceMs]);
  return [internalValue, setInternalValue];
}

// src/internal/styles.ts
import { styled as styled5 } from "styled-components";
import * as RadixDialog2 from "@radix-ui/react-dialog";
var Tray = styled5.div`
  position: absolute;
  right: 0px;
  height: 100%;
  width: 360px;
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.colors.orange100};
  border-left: 1px solid ${(p) => p.theme.divider};
  z-index: 1001;

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;
var TrayContent = styled5(RadixDialog2.Content)`
  position: absolute;
  top: 0px;
  right: 0px;
  bottom: 0px;
  width: 360px;
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.colors.orange100};
  border-left: 1px solid ${(p) => p.theme.divider};
  z-index: 1001;

  &:focus-visible {
    outline: none; // remove browser default outline
    &::before {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      outline: none;
      ${(p) => p.theme.focusInset}
    }
  }

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;
var Scrollable = styled5.div`
  flex-grow: 2;
  display: flex;
  flex-direction: column;

  overflow: auto;
  /* Remove scrollbar space */
  ::-webkit-scrollbar {
    width: 0px;
  }
`;

// src/manual-modal/modal/CurrentHeader.tsx
import { useContext as useContext4 } from "react";
import { jsx as jsx6 } from "react/jsx-runtime";
var CurrentHeader = () => {
  const { page, makeQuery, setOpen, goBack } = useContext4(ManualContext);
  const onClose = () => {
    makeQuery("");
    setOpen(false);
  };
  switch (page.pageType) {
    case "home" /* Home */: {
      return /* @__PURE__ */ jsx6(Header, { title: "Cadmus Manual", subtitle: "Home", onClose });
    }
    case "index" /* Category */: {
      return /* @__PURE__ */ jsx6(
        Header,
        {
          title: page.categoryName,
          subtitle: "Index",
          onClose,
          goBack
        }
      );
    }
    case "article" /* Article */: {
      return /* @__PURE__ */ jsx6(
        Header,
        {
          title: page.selectedArticle.title,
          subtitle: page.selectedArticle.category,
          onClose,
          goBack
        }
      );
    }
    case "result" /* Results */: {
      return /* @__PURE__ */ jsx6(
        Header,
        {
          title: "Search Results",
          subtitle: "Search",
          onClose,
          goBack: () => {
            makeQuery("");
          }
        }
      );
    }
  }
};

// src/manual-modal/modal/CurrentPage.tsx
import { useContext as useContext5 } from "react";

// src/components/pages/ArticlePage.tsx
import { styled as styled7 } from "styled-components";
import { Icon as Icon2 } from "@vericus/cadmus-icons";
import { IconButton as IconButton2, Spacer, Text as Text3 } from "@vericus/cadmus-ui";

// src/components/pages/ArticleContent.tsx
import * as React2 from "react";
import { styled as styled6 } from "styled-components";
import { jsx as jsx7 } from "react/jsx-runtime";
var Paper = styled6.div`
  font-family:
    "Open Sauce Two",
    ui-sans-serif,
    -apple-system;
  color: ${(p) => p.theme.colors.black100};

  p {
    font-weight: 400;
    font-size: 1em;
    margin-bottom: 1em;
    padding: 0 24px;
    line-height: 180%;
    letter-spacing: 0px;
    color: ${(p) => p.theme.colors.black100};
  }

  li > p {
    padding: 0px;
  }

  ul,
  ol {
    font-size: 15px;
    margin-bottom: 1em;
    line-height: 24px;
    letter-spacing: 0.2px;
    padding-left: 32px;
    padding-right: 24px;
    color: ${(p) => p.theme.colors.black100};
  }

  video,
  img {
    width: 100%;
  }

  table {
    width: 100%;
    margin-top: 1em;
    margin-bottom: 1em;
    padding: 0 24px;
    text-align: left;
  }

  td {
    padding: 6px 6px;
  }

  code {
    font-size: 0.875em;
    border: 1px solid ${(p) => p.theme.divider};
    background: ${(p) => p.theme.background.action2};
    border-radius: 3px;
    padding: 6px;
  }

  blockquote {
    font-size: inherit;
    border-left: 1px solid ${(p) => p.theme.divider};
    border-top: 1px solid transparent;
    border-bottom: 1px solid transparent;

    margin: 1em 0em;
    margin-left: 24px;
    padding: 0;
  }

  blockquote > p {
    color: ${(p) => p.theme.text.shade1};
  }

  li + li {
    margin-top: 0.25em;
  }

  h3 {
    font-weight: 600;
    color: ${(p) => p.theme.text.heading};
    font-size: 18px;
    margin-top: 3em;
    margin-bottom: 1em;
    padding: 0 24px;
    line-height: 24px;
    letter-spacing: 0.2px;
  }

  h4 {
    font-weight: 600;
    color: ${(p) => p.theme.text.heading};
    font-size: 1.25rem;
    margin-top: 3em;
    margin-bottom: 1em;
    padding: 0 24px;
    line-height: 120%;
    letter-spacing: 0px;
  }

  ol {
    list-style-position: outside;
    margin-left: 1em;
    margin-top: 1em;
  }

  ul {
    list-style: disc outside;
    margin-left: 1em;
    margin-top: 1em;
  }

  ul ul {
    list-style-type: circle;
    margin-top: 0.5em;
  }

  ul ul ul {
    list-style-type: square;
  }

  dd {
    margin-left: 1em;
  }

  figure {
    margin-left: 1em;
    margin-right: 1em;
    text-align: center;
  }

  figure:not(:first-child) {
    margin-top: 2em;
  }

  figure:not(:last-child) {
    margin-bottom: 2em;
  }

  figure img {
    display: inline-block;
  }

  figure figcaption {
    font-style: italic;
  }

  pre {
    -webkit-overflow-scrolling: touch;
    overflow-x: auto;
    padding: 1.25em 1.5em;
    white-space: pre;
    word-wrap: normal;
  }

  sup,
  sub {
    font-size: 75%;
  }

  a {
    color: ${(p) => p.theme.text.accent};
  }

  /* for keyboard shortcut related articles */
  #mac-toggle,
  #win-toggle,
  .mac-shortcuts,
  .win-shortcuts {
    display: none;
  }

  #mac-label,
  #win-label {
    cursor: pointer;

    &:hover {
      color: ${(p) => p.theme.background.action3};
    }
  }

  #mac-toggle:checked ~ .mac-shortcuts,
  #win-toggle:checked ~ .win-shortcuts {
    display: table;
  }

  #mac-toggle:checked ~ h4 > #mac-label,
  #win-toggle:checked ~ h4 > #win-label {
    color: ${(p) => p.theme.text.accent};
  }
`;
var ArticleContent = ({ content, openLink }) => {
  const innerHtml = {
    __html: content
  };
  const bodyEl = React2.useRef(null);
  const handleInternalClicks = (event) => {
    const target = event.target;
    if (bodyEl.current && target.href && bodyEl.current.contains(target)) {
      const { href } = target;
      if (href && typeof href === "string" && href.startsWith("manual://")) {
        event.preventDefault();
        const slug = href.slice(9);
        openLink(slug);
      }
    }
  };
  return /* @__PURE__ */ jsx7("div", { ref: bodyEl, onClick: handleInternalClicks, children: /* @__PURE__ */ jsx7(Paper, { dangerouslySetInnerHTML: innerHtml }) });
};

// src/components/pages/ArticlePage.tsx
import { jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
var Container3 = styled7.div`
  flex-grow: 2;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
var VoteBlock = styled7.div`
  justify-self: flex-end;
  display: flex;
  flex-direction: column;

  padding: 0 27px;

  background: ${(p) => p.theme.colors.orange100};
`;
var VoteIconContainer = styled7.div`
  height: 45px;
  padding: 0 45px;

  display: flex;
  justify-content: space-around;
  align-items: center;
`;
var ArticlePage = ({
  selectedArticle,
  onVote,
  changePage,
  articles,
  referencingStyle
}) => {
  const openLink = (slug) => {
    const replacedSlug = slug.replace("${REFERENCING_STYLE}", referencingStyle);
    const selectedArticle2 = articles.find((a) => a.slug === replacedSlug);
    if (selectedArticle2)
      changePage({ pageType: "article" /* Article */, selectedArticle: selectedArticle2 });
  };
  return /* @__PURE__ */ jsxs3(Container3, { children: [
    /* @__PURE__ */ jsx8(ArticleContent, { content: selectedArticle.content, openLink }),
    /* @__PURE__ */ jsx8(Spacer, { spacing: 108 }),
    /* @__PURE__ */ jsxs3(VoteBlock, { children: [
      /* @__PURE__ */ jsx8(Spacer, { spacing: 27 }),
      /* @__PURE__ */ jsx8(Text3, { kind: "headingSix", textAlign: "center", children: "Was this article helpful?" }),
      /* @__PURE__ */ jsx8(Spacer, { spacing: 27 }),
      /* @__PURE__ */ jsxs3(VoteIconContainer, { children: [
        /* @__PURE__ */ jsx8(
          IconButton2,
          {
            "aria-label": "upvote",
            selected: selectedArticle.vote === "Upvote" /* Upvote */,
            onClick: () => {
              onVote("Upvote" /* Upvote */, selectedArticle);
              changePage({
                pageType: "article" /* Article */,
                selectedArticle: { ...selectedArticle, vote: "Upvote" /* Upvote */ }
              });
            },
            children: /* @__PURE__ */ jsx8(Icon2, { iconName: "ThumbsUp" })
          }
        ),
        /* @__PURE__ */ jsx8(
          IconButton2,
          {
            "aria-label": "downvote",
            selected: selectedArticle.vote === "Downvote" /* DownVote */,
            onClick: () => {
              onVote("Downvote" /* DownVote */, selectedArticle);
              changePage({
                pageType: "article" /* Article */,
                selectedArticle: { ...selectedArticle, vote: "Downvote" /* DownVote */ }
              });
            },
            children: /* @__PURE__ */ jsx8(Icon2, { iconName: "ThumbsDown" })
          }
        )
      ] }),
      /* @__PURE__ */ jsx8(Spacer, { spacing: 36 })
    ] })
  ] });
};

// src/components/pages/CategoryPage.tsx
import { Spacer as Spacer3 } from "@vericus/cadmus-ui";

// src/components/pages/ArticleList.tsx
import { styled as styled8 } from "styled-components";
import { Icon as Icon3 } from "@vericus/cadmus-icons";
import { Colored, easeOutCirc, Spacer as Spacer2, Text as Text4 } from "@vericus/cadmus-ui";
import { Fragment, jsx as jsx9, jsxs as jsxs4 } from "react/jsx-runtime";
var ArticleLinkContainer = styled8.button`
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 40px;
  text-align: left;
  padding: 9px 27px;
  font-size: 15px;
  flex: none;

  border: none;
  background: transparent;
  color: ${(p) => p.theme.text.main};
  fill: ${(p) => p.theme.text.main};

  text-decoration: none;

  transition:
    background 0.3s ${easeOutCirc},
    fill 0.3s ${easeOutCirc};

  outline: none;
  cursor: pointer;

  &:not(:disabled) {
    &:hover,
    &:focus {
      background: ${(p) => p.theme.background.action1};
    }

    &:active {
      background: ${(p) => p.theme.background.action2};
    }
  }

  &:disabled {
    opacity: 0.36;
    cursor: not-allowed;
    text-decoration: line-through;
  }
`;
var ShowMore = styled8(Text4)`
  font-style: italic;
`;
var ArticleList = ({
  title,
  titleColor,
  articles,
  onArticleLinkClick,
  onShowMore
}) => {
  let displayArticles;
  if (onShowMore) {
    displayArticles = articles.slice(0, 3);
  } else {
    displayArticles = articles;
  }
  return /* @__PURE__ */ jsxs4(Fragment, { children: [
    /* @__PURE__ */ jsx9(
      Text4,
      {
        kind: "headingSix",
        color: "shade1",
        style: { padding: "0px 27px", margin: "9px 0px" },
        children: titleColor ? /* @__PURE__ */ jsx9(Colored, { color: titleColor, children: title }) : title
      }
    ),
    /* @__PURE__ */ jsx9(Spacer2, { spacing: 18 }),
    displayArticles.map((article) => /* @__PURE__ */ jsx9(
      ArticleLinkContainer,
      {
        onClick: () => onArticleLinkClick(article),
        children: /* @__PURE__ */ jsxs4(Text4, { kind: "bodyMd", color: "main", children: [
          article.title,
          article.url && /* @__PURE__ */ jsx9(StyledOpen, {})
        ] })
      },
      article.id
    )),
    onShowMore && /* @__PURE__ */ jsx9(ArticleLinkContainer, { onClick: onShowMore, children: /* @__PURE__ */ jsx9(ShowMore, { kind: "bodyLine", color: "shade1", children: "Show more" }) })
  ] });
};
var StyledOpen = styled8(Icon3).attrs({ iconName: "Open" })`
  margin-left: 9px;
  opacity: 0.36;
`;

// src/components/pages/CategoryPage.tsx
import { Fragment as Fragment2, jsx as jsx10, jsxs as jsxs5 } from "react/jsx-runtime";
function CategoryPage(props) {
  const { articles, categoryName, changePage } = props;
  return /* @__PURE__ */ jsxs5(Fragment2, { children: [
    /* @__PURE__ */ jsx10(Spacer3, { spacing: 18 }),
    /* @__PURE__ */ jsx10(
      ArticleList,
      {
        title: categoryName,
        articles: articles.filter((a) => a.category === categoryName),
        onArticleLinkClick: (article) => changePage({ pageType: "article" /* Article */, selectedArticle: article })
      }
    ),
    /* @__PURE__ */ jsx10(Spacer3, { spacing: 108 })
  ] });
}

// src/components/pages/HomePage.tsx
import * as React3 from "react";
import { styled as styled9 } from "styled-components";
import { Divider, Spacer as Spacer4, Text as Text5 } from "@vericus/cadmus-ui";
import { Fragment as Fragment3, jsx as jsx11, jsxs as jsxs6 } from "react/jsx-runtime";
var HomePage = ({ articles, changePage }) => {
  const items = React3.useMemo(() => {
    const categories = Array.from(
      new Set(articles.map((a) => a.category))
    ).sort();
    return categories.map((category) => ({
      category,
      articles: articles.filter((a) => a.category === category)
    }));
  }, [articles]);
  const a11yStmtItem = articles.find(
    (a) => a.slug === "accessibility_statement.md"
  );
  if (articles.length === 0) {
    return /* @__PURE__ */ jsx11(
      ArticleContent,
      {
        openLink: () => null,
        content: "<p>Sorry! Something went wrong, we could not load any articles. You may be experiencing internet connection issues. Please check your connection and try again.</p>"
      }
    );
  }
  return /* @__PURE__ */ jsxs6(Fragment3, { children: [
    /* @__PURE__ */ jsx11(Introduction, { kind: "systemMd", italic: true, children: "Our skills guides are here to help you through every stage of your assessment. Check them out and submit your best work." }),
    /* @__PURE__ */ jsx11(Divider, {}),
    /* @__PURE__ */ jsx11(Spacer4, { spacing: 18 }),
    a11yStmtItem && /* @__PURE__ */ jsx11(
      ArticleLinkContainer,
      {
        onClick: () => {
          changePage({
            pageType: "article" /* Article */,
            selectedArticle: a11yStmtItem
          });
        },
        children: /* @__PURE__ */ jsx11(Text5, { kind: "headingSix", children: a11yStmtItem.title })
      },
      a11yStmtItem.slug
    ),
    items.map((item) => /* @__PURE__ */ jsx11(
      ArticleLinkContainer,
      {
        onClick: () => {
          changePage({
            pageType: "index" /* Category */,
            categoryName: item.category
          });
        },
        children: /* @__PURE__ */ jsx11(Text5, { kind: "headingSix", children: item.category })
      },
      item.category
    )),
    /* @__PURE__ */ jsx11(Spacer4, { spacing: 27 }),
    /* @__PURE__ */ jsx11(
      ArticleLinkContainer,
      {
        as: "a",
        href: "https://support.cadmus.io/students",
        target: "_blank",
        children: /* @__PURE__ */ jsxs6(Text5, { kind: "bodyMd", color: "shade1", bold: true, children: [
          "Cadmus Help Docs",
          /* @__PURE__ */ jsx11(StyledOpen, {})
        ] })
      }
    ),
    /* @__PURE__ */ jsx11(Spacer4, { spacing: 108 })
  ] });
};
var Introduction = styled9(Text5)`
  padding: 9px 27px;
  margin: 9px 0px;
  color: hsl(245, 90%, 65%);
`;

// src/components/pages/ResultsPage.tsx
import { Spacer as Spacer6, Text as Text7 } from "@vericus/cadmus-ui";

// src/components/pages/HSArticleList.tsx
import { styled as styled10 } from "styled-components";
import { Colored as Colored2, colors, Spacer as Spacer5, Text as Text6 } from "@vericus/cadmus-ui";
import xss from "xss";
import { Fragment as Fragment4, jsx as jsx12, jsxs as jsxs7 } from "react/jsx-runtime";
var HSArticleList = (props) => {
  const { articles } = props;
  return /* @__PURE__ */ jsxs7(Fragment4, { children: [
    /* @__PURE__ */ jsx12(
      Text6,
      {
        kind: "headingSix",
        color: "shade1",
        style: { padding: "0px 27px", margin: "9px 0px" },
        children: /* @__PURE__ */ jsx12(Colored2, { color: "purple800", children: "Using Cadmus" })
      }
    ),
    /* @__PURE__ */ jsx12(Spacer5, { spacing: 18 }),
    articles.map((article) => /* @__PURE__ */ jsx12(
      Container4,
      {
        onClick: () => {
          const url = article.url.includes("support.cadmus.io") ? article.url : `https://${article.domain}${article.url}`;
          window.open(url, "_blank");
        },
        children: /* @__PURE__ */ jsxs7(Text6, { kind: "bodyMd", color: "main", children: [
          /* @__PURE__ */ jsx12(
            "span",
            {
              dangerouslySetInnerHTML: {
                __html: xss(article.title, {
                  whiteList: {
                    span: ["class"]
                  }
                })
              }
            }
          ),
          /* @__PURE__ */ jsx12(StyledOpen, {})
        ] })
      },
      article.id
    ))
  ] });
};
var Container4 = styled10(ArticleLinkContainer)`
  .hs-search-highlight {
    background-color: ${colors.yellowA500};
  }
`;

// src/components/pages/ResultsPage.tsx
import { Fragment as Fragment5, jsx as jsx13, jsxs as jsxs8 } from "react/jsx-runtime";
function ResultsPage(props) {
  const { results, changePage, hsSearchResults } = props;
  const length = results.length + hsSearchResults.length;
  const title = `${length} article${length === 1 ? "" : "s"} found`;
  return /* @__PURE__ */ jsxs8(Fragment5, { children: [
    /* @__PURE__ */ jsx13(
      Text7,
      {
        kind: "headingSix",
        color: "shade1",
        style: { padding: "0px 27px", margin: "9px 0px" },
        children: title
      }
    ),
    results.length > 0 && /* @__PURE__ */ jsxs8(Fragment5, { children: [
      /* @__PURE__ */ jsx13(
        ArticleList,
        {
          title: "University Resources",
          titleColor: "orange500",
          articles: results,
          onArticleLinkClick: (article) => changePage({
            pageType: "article" /* Article */,
            selectedArticle: article
          })
        }
      ),
      /* @__PURE__ */ jsx13(Spacer6, { spacing: 27 })
    ] }),
    hsSearchResults.length > 0 && /* @__PURE__ */ jsx13(HSArticleList, { articles: hsSearchResults }),
    /* @__PURE__ */ jsx13(Spacer6, { spacing: 27 })
  ] });
}

// src/manual-modal/modal/CurrentPage.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
var CurrentPage = () => {
  const {
    articles,
    changePage,
    page,
    referencingStyle,
    onVote,
    hsSearchResults,
    results
  } = useContext5(ManualContext);
  switch (page.pageType) {
    case "home" /* Home */:
      return /* @__PURE__ */ jsx14(HomePage, { articles, changePage });
    case "index" /* Category */:
      return /* @__PURE__ */ jsx14(
        CategoryPage,
        {
          articles,
          changePage,
          categoryName: page.categoryName
        }
      );
    case "article" /* Article */:
      return /* @__PURE__ */ jsx14(
        ArticlePage,
        {
          articles,
          changePage,
          referencingStyle,
          onVote,
          selectedArticle: page.selectedArticle
        }
      );
    case "result" /* Results */:
      return /* @__PURE__ */ jsx14(
        ResultsPage,
        {
          changePage,
          hsSearchResults,
          results
        }
      );
  }
};

// src/manual-modal/modal/ManualModal.tsx
import { jsx as jsx15, jsxs as jsxs9 } from "react/jsx-runtime";
function ManualModal(props) {
  const { mountElement, trigger, onAskHelp } = props;
  const { isOpen, setOpen, page, query, makeQuery } = useContext6(ManualContext);
  const scrollbarRef = useRef2(null);
  useEffect3(() => {
    const scrollbar = scrollbarRef.current;
    if (scrollbar) scrollbar.scrollTo({ top: 0 });
  }, [page.pageType, scrollbarRef]);
  return /* @__PURE__ */ jsxs9(RadixDialog3.Root, { open: isOpen, onOpenChange: setOpen, children: [
    trigger && /* @__PURE__ */ jsx15(RadixDialog3.Trigger, { asChild: true, children: trigger }),
    /* @__PURE__ */ jsx15(RadixDialog3.Portal, { container: mountElement.current, children: /* @__PURE__ */ jsxs9(TrayContent, { onEscapeKeyDown: () => setOpen(false), children: [
      /* @__PURE__ */ jsxs9(Scrollable, { ref: scrollbarRef, children: [
        /* @__PURE__ */ jsxs9("div", { children: [
          /* @__PURE__ */ jsx15(CurrentHeader, {}),
          /* @__PURE__ */ jsx15(Divider2, {}),
          page.pageType !== "article" /* Article */ && /* @__PURE__ */ jsx15(SearchBox, { query, makeQuery }),
          /* @__PURE__ */ jsx15(Divider2, {})
        ] }),
        /* @__PURE__ */ jsx15(CurrentPage, {})
      ] }),
      onAskHelp && /* @__PURE__ */ jsx15(Help, { onAskHelp })
    ] }) })
  ] });
}
export {
  ManualContext,
  ManualModal,
  ManualProvider,
  ManualTriggers,
  manualPresets,
  useManualOpen,
  useManualPreset
};
//# sourceMappingURL=index.mjs.map